export function Features() {
  return (
    <section id="features" className="relative py-28 px-6 lg:px-8">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink leading-[1.1]">
            Four capabilities. The full consent lifecycle.
          </h2>
          <p className="mt-4 text-[16px] text-sub leading-relaxed">
            Not a grid of eight identical tiles. Four things that matter, with proof that they work.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {/* Card 1 */}
          <div className="card-lift bg-card border border-line rounded-xl3 p-7">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue mb-5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-[16px] text-ink mb-2">API First, always</h3>
            <p className="text-[13.5px] text-sub leading-relaxed mb-4">
              Every capability visible in the dashboard is available as an API call before it appears in the UI.{' '}
              <code className="mono text-[12px] bg-surface px-1.5 py-0.5 rounded border border-line text-ink">
                POST /v1/consent/record
              </code>{' '}
              — the endpoint you will use in production is the same one this page demos.
            </p>
            <div className="mono text-[11.5px] text-muted border-t border-line pt-4">
              200 OK · avg 42ms p50 · 99.98% uptime
            </div>
          </div>

          {/* Card 2 */}
          <div className="card-lift bg-card border border-line rounded-xl3 p-7">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue mb-5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="font-semibold text-[16px] text-ink mb-2">Immutable audit trail</h3>
            <p className="text-[13.5px] text-sub leading-relaxed mb-4">
              Once written, a consent record cannot be edited, deleted, or backfilled. Not by your team. Not by ours. Not by a database migration. Append-only at the storage layer — not a policy, an architecture.
            </p>
            <div className="mono text-[11.5px] text-muted border-t border-line pt-4">
              Zero records modified after creation · since launch
            </div>
          </div>

          {/* Card 3 */}
          <div className="card-lift bg-card border border-line rounded-xl3 p-7">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue mb-5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <h3 className="font-semibold text-[16px] text-ink mb-2">Versioned notices, permanent references</h3>
            <p className="text-[13.5px] text-sub leading-relaxed mb-4">
              Each notice is content-addressed by its SHA hash. A consent record references the exact hash of the notice shown at the moment of capture. No retroactive version drift. Regulators can verify what the user actually agreed to.
            </p>
            <div className="mono text-[11.5px] text-muted border-t border-line pt-4">
              sha:a3f9c1b2 · privacy-v4.2 · EN/HI/MR
            </div>
          </div>

          {/* Card 4 */}
          <div className="card-lift bg-card border border-line rounded-xl3 p-7">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue mb-5">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-semibold text-[16px] text-ink mb-2">Certificates, on demand</h3>
            <p className="text-[13.5px] text-sub leading-relaxed mb-4">
              Quarterly compliance certificates generated from your actual consent data. Signed, timestamped, exportable as PDF. Available within 500 milliseconds of the quarter close. Your legal team shares them with a regulator without involving engineering.
            </p>
            <div className="mono text-[11.5px] text-muted border-t border-line pt-4">
              CERT-2026-0091 · Q2 2026 · 228,410 events
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
